import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Settings, Upload, Save, RefreshCw, Infinity, AlertCircle, CheckCircle,
  HardDrive, FileImage, Layers
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const TierCard = ({ tier, tierName, limits, onChange, disabled }) => {
  const isUnlimitedFileSize = limits.max_file_size_mb === null;
  const isUnlimitedFiles = limits.max_files_per_upload === null;
  
  // Calculate max total size
  const maxTotalSize = (limits.max_file_size_mb !== null && limits.max_files_per_upload !== null)
    ? limits.max_file_size_mb * limits.max_files_per_upload
    : null;
  
  const tierColors = {
    starter: "from-slate-500 to-slate-600",
    pro: "from-blue-500 to-blue-600",
    studio: "from-purple-500 to-purple-600"
  };
  
  const tierDescriptions = {
    starter: "Service fulfillment (single-client delivery)",
    pro: "Photo commerce + automation",
    studio: "Teams, analytics, and scale"
  };

  return (
    <Card className="bg-card border-border overflow-hidden">
      <div className={`h-2 bg-gradient-to-r ${tierColors[tier]}`} />
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 capitalize">
          <Layers className="w-5 h-5" />
          {tierName}
        </CardTitle>
        <CardDescription>{tierDescriptions[tier]}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Max File Size */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <FileImage className="w-4 h-4 text-muted-foreground" />
              Max File Size
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Unlimited</span>
              <Switch
                checked={isUnlimitedFileSize}
                onCheckedChange={(checked) => {
                  onChange(tier, "max_file_size_mb", checked ? null : 25);
                }}
                disabled={disabled}
              />
            </div>
          </div>
          {!isUnlimitedFileSize && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                value={limits.max_file_size_mb || ""}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  onChange(tier, "max_file_size_mb", val);
                }}
                disabled={disabled}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">MB per file</span>
            </div>
          )}
          {isUnlimitedFileSize && (
            <div className="flex items-center gap-2 text-sm text-green-500">
              <Infinity className="w-4 h-4" />
              No file size limit
            </div>
          )}
        </div>

        {/* Max Files */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-muted-foreground" />
              Max Files per Upload
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Unlimited</span>
              <Switch
                checked={isUnlimitedFiles}
                onCheckedChange={(checked) => {
                  onChange(tier, "max_files_per_upload", checked ? null : 100);
                }}
                disabled={disabled}
              />
            </div>
          </div>
          {!isUnlimitedFiles && (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                value={limits.max_files_per_upload || ""}
                onChange={(e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  onChange(tier, "max_files_per_upload", val);
                }}
                disabled={disabled}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">files</span>
            </div>
          )}
          {isUnlimitedFiles && (
            <div className="flex items-center gap-2 text-sm text-green-500">
              <Infinity className="w-4 h-4" />
              No file count limit
            </div>
          )}
        </div>

        {/* Calculated Max Total Size */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <HardDrive className="w-4 h-4 text-muted-foreground" />
            <Label className="text-muted-foreground">Calculated Max Total Size</Label>
          </div>
          <div className="text-lg font-semibold">
            {maxTotalSize !== null ? (
              <span>
                {maxTotalSize >= 1000 
                  ? `${(maxTotalSize / 1000).toFixed(1)} GB` 
                  : `${maxTotalSize} MB`
                }
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({limits.max_file_size_mb} MB × {limits.max_files_per_upload} files)
                </span>
              </span>
            ) : (
              <span className="flex items-center gap-2 text-green-500">
                <Infinity className="w-5 h-5" />
                Unlimited
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminSettings = () => {
  const [limits, setLimits] = useState(null);
  const [originalLimits, setOriginalLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/upload-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLimits(response.data.limits);
      setOriginalLimits(JSON.parse(JSON.stringify(response.data.limits)));
      setLastUpdated(response.data.updated_at);
    } catch (error) {
      toast.error("Failed to load upload limits");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (tier, field, value) => {
    setLimits(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.put(
        `${API}/admin/upload-limits`,
        limits,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOriginalLimits(JSON.parse(JSON.stringify(limits)));
      setLastUpdated(new Date().toISOString());
      toast.success("Upload limits saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save upload limits");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLimits(JSON.parse(JSON.stringify(originalLimits)));
    toast.info("Changes reset to last saved values");
  };

  const hasChanges = JSON.stringify(limits) !== JSON.stringify(originalLimits);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif tracking-tight mb-2">Upload Settings</h1>
          <p className="text-muted-foreground">
            Configure bulk upload limits for each subscription tier
          </p>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </div>
        
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || saving}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md"
        >
          <AlertCircle className="w-5 h-5 text-yellow-500" />
          <p className="text-sm text-yellow-500">
            You have unsaved changes. Click "Save Changes" to apply them.
          </p>
        </motion.div>
      )}

      {/* Info Card */}
      <Card className="bg-muted/30 border-border">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Upload className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1">How Upload Limits Work</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• <strong>Max File Size:</strong> Maximum size for each individual file</li>
                <li>• <strong>Max Files:</strong> Maximum number of files per upload session</li>
                <li>• <strong>Max Total Size:</strong> Automatically calculated (File Size × Files)</li>
                <li>• Toggle "Unlimited" to remove restrictions for a field</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tier Cards */}
      {limits && (
        <div className="grid md:grid-cols-3 gap-6">
          <TierCard
            tier="starter"
            tierName="Starter ($19/mo)"
            limits={limits.starter}
            onChange={handleChange}
            disabled={saving}
          />
          <TierCard
            tier="pro"
            tierName="Pro ($39/mo)"
            limits={limits.pro}
            onChange={handleChange}
            disabled={saving}
          />
          <TierCard
            tier="studio"
            tierName="Studio ($79/mo)"
            limits={limits.studio}
            onChange={handleChange}
            disabled={saving}
          />
        </div>
      )}

      {/* Summary Table */}
      {limits && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg">Limits Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Tier</th>
                    <th className="text-center py-3 px-4 font-medium">Max File Size</th>
                    <th className="text-center py-3 px-4 font-medium">Max Files</th>
                    <th className="text-center py-3 px-4 font-medium">Max Total Size</th>
                  </tr>
                </thead>
                <tbody>
                  {["starter", "pro", "studio"].map((tier) => {
                    const tierLimits = limits[tier];
                    const totalSize = (tierLimits.max_file_size_mb && tierLimits.max_files_per_upload)
                      ? tierLimits.max_file_size_mb * tierLimits.max_files_per_upload
                      : null;
                    
                    return (
                      <tr key={tier} className="border-b border-border/50">
                        <td className="py-3 px-4 capitalize font-medium">{tier}</td>
                        <td className="py-3 px-4 text-center">
                          {tierLimits.max_file_size_mb !== null 
                            ? `${tierLimits.max_file_size_mb} MB`
                            : <span className="text-green-500">Unlimited</span>
                          }
                        </td>
                        <td className="py-3 px-4 text-center">
                          {tierLimits.max_files_per_upload !== null 
                            ? tierLimits.max_files_per_upload
                            : <span className="text-green-500">Unlimited</span>
                          }
                        </td>
                        <td className="py-3 px-4 text-center">
                          {totalSize !== null 
                            ? (totalSize >= 1000 ? `${(totalSize/1000).toFixed(1)} GB` : `${totalSize} MB`)
                            : <span className="text-green-500">Unlimited</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminSettings;
